import { dispatch } from 'pluginServices/store';
import _ from 'lodash';
import { fromJS } from 'immutable';
import { pushToast } from 'containers/ToastManager/actions';
import gtmService from 'utils/gtm/gtmService';
import { httpErrorCodes, GTM_EVENT_TYPES } from 'utils/gtm/constants';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import * as constants from './constants';

/**
 * Executes a command over the RTM.
 * @param {RTMConnection} connection A shared & active RTM connection
 * @param {Object} message An RTM message.
 * @param {string} responseTypeOk The response type of a success case
 * @param {string} responseTypeError The response type of failure case
 * @param {string} [customErrorMessage] Error message to be displayed to user during failure case
 * @param {boolean} returnError Flag for returning the error information to callee
 */
export const fetchFromRTM = async (connection, message, responseTypeOk, responseTypeError, customErrorMessage, returnError) => {
  let response;
  const responseMsg = await connection.fetch(message);

  if (responseMsg.type === responseTypeOk) {
    response = await responseMsg.response || responseTypeOk;
  } else if (responseMsg.type === constants.RTM_AUTHOR_ACTIVITY_GET_ERROR &&
    responseMsg.code.toString() === httpErrorCodes.UNAUTHORIZED) {
    window.location.href = '/unauthorized';
  } else if (responseMsg.type === constants.RTM_WORKSPACE_PROJECT_GET_ERROR &&
    responseMsg.code.toString() === httpErrorCodes.UNAUTHORIZED) {
    window.location.href = `/courseware/${message.projectId}/unauthorized`;
  } else if (responseMsg.type === constants.RTM_AUTHOR_ACTIVITY_GET_ERROR &&
    responseMsg.code.toString() === httpErrorCodes.NOT_FOUND) {
    window.location.href = '/unavailable';
  } else if (responseMsg.type === constants.RTM_WORKSPACE_PROJECT_GET_ERROR &&
    responseMsg.code.toString() === httpErrorCodes.NOT_FOUND) {
    window.location.href = `/courseware/${message.projectId}/unavailable`;
  } else if (responseMsg.type === responseTypeError) {
    createRTMErrorEvent(responseMsg.message, httpErrorCodes.UNPROCESSABLE_ENTITY); // error event pushed to GTM
    if (returnError) {
      return { error: { code: responseMsg.code, message: responseMsg.message } };
    }
    const toastMessage = customErrorMessage || responseMsg.message;
    dispatch(pushToast({ type: 'simple', message: toastMessage }));
  }
  return response;
};

/**
 * Executes a command over the RTM.  The result is memoized, so that the
 * same request isn't remade multiple times.
 * @param {RTMConnection} connection A shared & active RTM connection
 * @param {Object} message An RTM message.
 * @param {string} responseTypeOk The response type of a success case
 * @param {string} responseTypeError The response type of failure case
 */
export const fetchFromRTMMemoized = _.memoize(fetchFromRTM, (connection, message) => fromJS(message).hashCode());

/**
 * Subscribes to a RTM resource.
 * @param {RTMConnection} connection A shared & active RTM connection
 * @param {Object} message An RTM message.
 * @param {function(*)} listener A callback that is invoked when the
 *   resource changes
 */
export const subscribeToRTM = async (connection, message, listener) => {
  try {
    const subscription = await connection.subscribe(message, listener);
    return subscription;
  } catch (errorMessage) {
    createRTMErrorEvent(errorMessage, httpErrorCodes.BAD_REQUEST);
    const rtmErrorMessage = {
      id: 'rtmErrorMessage',
      defaultMessage: `subscribeToRTM Error : ${errorMessage?.message || errorMessage}`,
    };
    const errorTextLower = String(errorMessage?.message ?? errorMessage ?? '').toLowerCase();
    /** Error substrings for subscribeToRTM: log only, no user toast */
    const SUBSCRIBE_RTM_CONSOLE_ONLY_ERROR_PHRASES = [
      'subscription already exists',
    ];
    if (SUBSCRIBE_RTM_CONSOLE_ONLY_ERROR_PHRASES.some((phrase) =>
      errorTextLower.includes(phrase.toLowerCase()))) {
      // eslint-disable-next-line no-console
      console.error('subscribeToRTM Error: ', errorMessage);
    } else {
      dispatch(pushToast({ message: intl.formatMessage(rtmErrorMessage) }));
    }
    return null;
  }
};

/**
 * Creates an error event for tracking purpose
 * @param {*} message Reason for error
 * @param {*} httpErrorCode
 */
const createRTMErrorEvent = (message, httpErrorCode) => {
  const pageObject = { pageName: window.location.pathname };
  const pageError = {
    errorCode: httpErrorCode,
    errorUrl: window.location.pathname,
    errorMessage: message,
    errorStateCode: httpErrorCode,
  };
  gtmService.gtmTrackEvent({eventType: GTM_EVENT_TYPES.PAGEVIEW, payload: {...pageObject,...pageError}});
};
